/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable import/order */
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import {
  selectUpdateConfiguration,
  setUpdatingConfiguration,
  updateConfiguration
} from "../../Common/store/slices/common";
import { updateConfigurationApi } from "../../Common/utils/api/api";
import t from "../../Common/utils/translation/translator";
import useCheckMobileScreen from "../../Common/utils/useCheckMobileScreen";
import {
  resetAxesToCompare,
  selectAxisOptions,
  selectAxisSelection,
  selectCartesianConfiguration,
  selectCartesianCurrentStep,
  selectCartesianReadOnlyMode,
  selectCartesianSteps,
  selectDynamics,
  selectKinematics,
  selectMechanics,
  setAxisOptions,
  setAxisSelection,
  setCartesianAdaptationKitToBundleQuantity,
  setCartesianAdaptationKitWithInBundleQuantity,
  setCartesianAxisQuantity,
  setCartesianCadenasProductCode,
  setCartesianCurrentStep,
  setCartesianDriveQuantity,
  setCartesianProductCode,
  setDynamicsHash,
  setMechanicsHash
} from "../store/slices/cartesian";

import Stepper from "shared-components/lib/iac-stepper";
import {
  buildVirtualPageViewDataLayerEvent,
  logDataLayerEvent
} from "../../Common/utils/dataLayerAnalytics/dataLayerAnalytics";
import {
  DEFAULT_AXIS_OPTIONS,
  DEFAULT_AXIS_SELECTION,
  getAdaptationKitToBundleQty,
  getAdaptationKitWithinBundleQty,
  getAxisQty,
  getDriveQuantity,
  getDynamicsHash,
  getMechanicsHash
} from "../store/data/cartesianData";

/**
 * Sub header of the app.
 */
const CartesianStepper = () => {
  const steps = useSelector(selectCartesianSteps);
  const currentStep = useSelector(selectCartesianCurrentStep);
  const configuration = useSelector(selectCartesianConfiguration);
  const dispatch = useDispatch();
  const isMobile = useCheckMobileScreen();

  function updateBOMQuantity(updatebillOfMaterials, dispatch) {
    const driveQuantity = getDriveQuantity(updatebillOfMaterials);
    dispatch(setCartesianDriveQuantity(driveQuantity));

    const adaptationKitWithinBundleQty = getAdaptationKitWithinBundleQty(updatebillOfMaterials);
    dispatch(setCartesianAdaptationKitWithInBundleQuantity(adaptationKitWithinBundleQty));

    const adaptationKitToBundleQty = getAdaptationKitToBundleQty(updatebillOfMaterials);
    dispatch(setCartesianAdaptationKitToBundleQuantity(adaptationKitToBundleQty));

    const axisQty = getAxisQty(updatebillOfMaterials);
    dispatch(setCartesianAxisQuantity(axisQty));
  }

  useEffect(() => {
    logDataLayerEvent(buildVirtualPageViewDataLayerEvent(configuration?.configId, currentStep?.label));
  }, [currentStep]);

  const runUpdateConfiguration = useSelector(selectUpdateConfiguration);

  const modeReadOnly = useSelector(selectCartesianReadOnlyMode);
  const queryClient = useQueryClient();

  const [savedConfigurationPayload, setSavedConfigurationPayload] = useState(null);

  const {
    refetch: execUpdateConfiguration,
    isLoading,
    isFetching
  } = useQuery(
    "UPDATE_CARTESIAN_CONFIGURATION",
    async () => {
      try {
        if (JSON.stringify(savedConfigurationPayload) !== JSON.stringify(configuration.configData)) {
          setSavedConfigurationPayload(configuration.configData);
          dispatch(setCartesianProductCode(""));
          dispatch(setCartesianCadenasProductCode(""));
          const updatedConfig = (await updateConfigurationApi(configuration.configId, configuration.configData)) || {};
          if (updatedConfig) {
            const updatedProductCode = updatedConfig.updatedConfigurationData.configData.productCode;
            const updatedCadenasProductCode = updatedConfig.updatedConfigurationData.configData.cadenasProductCode;
            const updatebillOfMaterials = updatedConfig.updatedConfigurationData.configData.billOfMaterials;
            console.log("updatebillOfMaterials", updatebillOfMaterials);
            updateBOMQuantity(updatebillOfMaterials, dispatch);
            dispatch(setCartesianProductCode(updatedProductCode));
            dispatch(setCartesianCadenasProductCode(updatedCadenasProductCode));
          }
          return updatedConfig;
        }

        return null;
      } catch (error) {
        console.error("ERR", error);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    dispatch(setUpdatingConfiguration(isLoading || isFetching));
  }, [isLoading, isFetching]);

  useEffect(() => {
    if (configuration?.configId && currentStep && !modeReadOnly) {
      // console.log("Configuration changed => update CONF");
      queryClient.cancelQueries("UPDATE_CONFIGURATION");
      execUpdateConfiguration();
    }
  }, [runUpdateConfiguration, configuration]);

  const handleSelectionSettings = (step) => {
    dispatch(setCartesianCurrentStep(step));
    dispatch(updateConfiguration());
  };

  const kinematics = useSelector(selectKinematics);
  const mechanics = useSelector(selectMechanics);
  const dynamics = useSelector(selectDynamics);
  const axisSelection = useSelector(selectAxisSelection);
  const axisOptions = useSelector(selectAxisOptions);

  const isKinematicsValid = () => kinematics.isValid;
  const isMechanicsValid = () => mechanics.isValid;
  const isDynamicsValid = () => dynamics.isValid;
  const isAxisSelectionValid = () => axisSelection.isValid;
  const isAxisOptionsValid = () => axisOptions.isValid;

  const handleGoToMechanics = () => {
    if (isKinematicsValid()) {
      dispatch(setCartesianCurrentStep(steps.MECHANICS));
      dispatch(updateConfiguration());
    }
  };

  const handleGoToDynamics = () => {
    if (isKinematicsValid() && isMechanicsValid()) {
      dispatch(setCartesianCurrentStep(steps.DYNAMICS));
      dispatch(updateConfiguration());
      const mechanicsHash = getMechanicsHash(mechanics);
      //Reset dynamics and Axis Selection if hash is different
      if (mechanicsHash !== mechanics.hash) {
        //dispatch(setDynamics(DEFAULT_DYNAMICS));
        dispatch(setAxisSelection(DEFAULT_AXIS_SELECTION));
        dispatch(resetAxesToCompare());
        dispatch(setAxisOptions(DEFAULT_AXIS_OPTIONS));
        dispatch(setMechanicsHash(mechanicsHash));
      }
    }
  };

  const handleGoToAxisSelection = () => {
    if (isKinematicsValid() && isMechanicsValid() && isDynamicsValid()) {
      dispatch(setCartesianCurrentStep(steps.AXIS_SELECTION));
      dispatch(updateConfiguration());
      const dynamicsHash = getDynamicsHash(dynamics);
      //Reset axis selection and Axis Selection if hash is different
      if (dynamicsHash !== dynamics.hash) {
        dispatch(setAxisSelection(DEFAULT_AXIS_SELECTION));
        dispatch(resetAxesToCompare());
        dispatch(setAxisOptions(DEFAULT_AXIS_OPTIONS));
        dispatch(setDynamicsHash(dynamicsHash));
      }
    }
  };

  const handleGoToAxisOption = () => {
    if (isKinematicsValid() && isMechanicsValid() && isDynamicsValid() && isAxisSelectionValid()) {
      dispatch(setCartesianCurrentStep(steps.AXIS_OPTIONS));
      dispatch(updateConfiguration());
    }
  };

  const cartesianStepper = currentStep !== steps.START && (
    <>
      <Stepper
        steps={[
          {
            action: () => handleSelectionSettings(steps.KINEMATICS),
            active: true,
            done: isKinematicsValid(),
            hidden: false,
            id: "KINEMATICS",
            isActiveStep: currentStep === steps.KINEMATICS,
            isActive: currentStep === steps.KINEMATICS,
            name: steps.KINEMATICS.label,
            order: 1,
            skipped: false,
            subLabel:
              currentStep === steps.KINEMATICS
                ? t("msizer.ui.stepper.in_progress")
                : configuration?.configData?.kinematics?.type
          },
          {
            action: () => handleGoToMechanics(),
            active: isKinematicsValid(),
            done: isMechanicsValid(),
            hidden: false,
            id: "MECHANICS",
            isActiveStep: currentStep === steps.MECHANICS,
            isActive: currentStep === steps.MECHANICS,
            name: steps.MECHANICS.label,
            order: 2,
            skipped: false,
            subLabel:
              currentStep === steps.MECHANICS
                ? t("msizer.ui.stepper.in_progress")
                : isMechanicsValid()
                  ? t("msizer.ui.stepper.done")
                  : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToDynamics(),
            active: isKinematicsValid() && isMechanicsValid(),
            done: isDynamicsValid(),
            hidden: false,
            id: "DYNAMICS",
            isActiveStep: currentStep === steps.DYNAMICS,
            isActive: currentStep === steps.DYNAMICS,
            name: steps.DYNAMICS.label,
            order: 3,
            skipped: false,
            subLabel:
              currentStep === steps.DYNAMICS
                ? t("msizer.ui.stepper.in_progress")
                : isDynamicsValid()
                  ? t("msizer.ui.stepper.done")
                  : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToAxisSelection(),
            active: isKinematicsValid() && isMechanicsValid() && isDynamicsValid(),
            done: isAxisSelectionValid(),
            hidden: false,
            id: "AXIS_SELECTION",
            isActiveStep: currentStep === steps.AXIS_SELECTION,
            isActive: currentStep === steps.AXIS_SELECTION,
            name: steps.AXIS_SELECTION.label,
            order: 4,
            skipped: false,
            subLabel:
              currentStep === steps.AXIS_SELECTION
                ? t("msizer.ui.stepper.in_progress")
                : isAxisSelectionValid()
                  ? t("msizer.ui.stepper.done")
                  : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToAxisOption(),
            active: isKinematicsValid() && isMechanicsValid() && isDynamicsValid() && isAxisSelectionValid(),
            done: isAxisOptionsValid(),
            hidden: false,
            id: "AXIS_OPTIONS",
            isActiveStep: currentStep === steps.AXIS_OPTIONS,
            isActive: currentStep === steps.AXIS_OPTIONS,
            name: steps.AXIS_OPTIONS.label,
            order: 5,
            skipped: false,
            subLabel:
              currentStep === steps.AXIS_OPTIONS
                ? t("msizer.ui.stepper.in_progress")
                : isAxisOptionsValid()
                  ? t("msizer.ui.stepper.done")
                  : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleSelectionSettings(steps.FINALIZE_CARTESIAN),
            active:
              isKinematicsValid() &&
              isMechanicsValid() &&
              isDynamicsValid() &&
              isAxisSelectionValid() &&
              isAxisOptionsValid(),
            done: currentStep === steps.FINALIZE_CARTESIAN,
            hidden: false,
            id: "FINALIZE_CARTESIAN",
            isActiveStep: currentStep === steps.FINALIZE_CARTESIAN,
            isActive: currentStep === steps.FINALIZE_CARTESIAN,
            name: steps.FINALIZE_CARTESIAN.label,
            order: 6,
            skipped: false,
            subLabel:
              currentStep === steps.FINALIZE_CARTESIAN ? t("msizer.ui.stepper.done") : t("msizer.ui.stepper.not_done")
          }
        ]}
        mobileBreakpoint={800}
        disabled={false}
      ></Stepper>
    </>
  );

  return cartesianStepper;
};

CartesianStepper.propTypes = {};

CartesianStepper.defaultProps = {};

export default CartesianStepper;
